/**
 * useArchivedTemplateCheck
 *
 * Checks whether the template referenced by the currently loaded LOR document
 * is still in an ACTIVE state. If the template has been archived or superseded
 * since the document was generated, returns `isTemplateArchived: true` so the
 * UI can block send actions and prompt the user to regenerate with an active
 * template.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';

import type { TemplateStatus } from '@/services/api/generated/templatesV2Service';
import { useTemplateRetrieveQuery } from '@/services/api/generated/templatesV2Service';

import type { MatterDocLite } from '../../types';

interface ArchivedTemplateCheckResult {
  /** Whether the current document's template is archived, superseded, deleted, or missing. */
  isTemplateArchived: boolean;
  /** The status of the template (ACTIVE, ARCHIVED, SUPERSEDED), or null if unknown/missing. */
  templateStatus: TemplateStatus | null;
  /** The name of the archived template for display purposes. */
  templateName: string | null;
  /** Whether the template status is still loading. */
  isLoading: boolean;
  /** Whether the template retrieve query errored (e.g. 404 — template deleted). */
  isError: boolean;
}

/**
 * Given the list of LOR documents for a coverage, checks whether the template
 * used by the most recent document is still active.
 *
 * @param lorDocsForCoverage LOR documents filtered to the current coverage
 */
export const useArchivedTemplateCheck = (
  lorDocsForCoverage: MatterDocLite[],
): ArchivedTemplateCheckResult => {
  // Find the template ID used by the most recent LOR document for this coverage
  const docTemplateId = useMemo(() => {
    if (!lorDocsForCoverage.length) return null;
    // The most recent doc is first (sorted by created_at desc in useLorDocuments)
    return lorDocsForCoverage[0]?.document_template?.id ?? null;
  }, [lorDocsForCoverage]);

  // We check the template that the existing document was built with.
  // If the user has already switched to a different (active) template in the
  // selector, we still want to warn about the *document's* template being
  // archived — because the document content still reflects the old template.
  const templateIdToCheck = docTemplateId ?? null;

  const {
    data: templateData,
    isLoading,
    isError,
  } = useTemplateRetrieveQuery(
    templateIdToCheck
      ? { id: templateIdToCheck }
      : (skipToken as unknown as { id: string }),
  );

  // A template is considered "not usable" if:
  // 1. The fetch errored (e.g. 404 — template was deleted)
  // 2. The template data came back but status is not ACTIVE
  // 3. The template has a non-null deleted_at timestamp
  // In all these cases we want to block send and prompt regeneration.
  const isTemplateArchived = useMemo(() => {
    // If we don't have a template ID to check there are no docs yet — nothing to block
    if (!templateIdToCheck) return false;
    // If the query errored (404 / network failure) the template is gone — flag it
    if (isError) return true;
    // Still loading — don't flag yet (buttons are disabled separately via isLoading)
    if (!templateData) return false;
    // Check explicit status and deleted_at
    return templateData.status !== 'ACTIVE' || !!templateData.deleted_at;
  }, [templateIdToCheck, isError, templateData]);

  const templateStatus = (templateData?.status as TemplateStatus) ?? null;
  const templateName = templateData?.name ?? null;

  return {
    isTemplateArchived,
    templateStatus,
    templateName,
    isLoading,
    isError,
  };
};
